import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Music as MusicIcon, Play } from 'lucide-react';
import { musicApi } from '@/api/music';

/**
 * TikTok "sound page": обложка + метаданные трека + grid всех сторис
 * с этим треком. Открывается по клику на музыкальную плашку в ForYouFeed.
 */
export default function SoundPage({
  trackId,
  onClose,
  onOpenStory,
}: {
  trackId: string;
  onClose: () => void;
  onOpenStory?: (storyId: string) => void;
}) {
  const { data } = useQuery({
    queryKey: ['music', trackId, 'stories'],
    queryFn: () => musicApi.storiesForTrack(trackId),
  });

  return (
    <div className="fixed inset-0 z-[60] bg-surface-950 text-white flex flex-col">
      <div className="flex items-center gap-3 p-4 border-b border-surface-800">
        <button onClick={onClose}><ArrowLeft size={20} /></button>
        <MusicIcon size={18} />
        <span className="font-semibold">Звук</span>
      </div>

      {data && (
        <div className="flex items-center gap-4 p-5 border-b border-surface-800">
          <div className="w-20 h-20 rounded-2xl bg-surface-800 overflow-hidden flex items-center justify-center">
            {data.track.coverUrl ? (
              <img src={data.track.coverUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <MusicIcon size={32} className="text-surface-500" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-lg truncate">{data.track.title}</div>
            <div className="text-sm text-surface-400 truncate">{data.track.artist}</div>
            <div className="text-xs text-surface-500 mt-1">
              {data.track.usageCount} сторис · {formatDur(data.track.durationSec)}
            </div>
          </div>
          <button
            onClick={() => { const a = new Audio(data.track.audioUrl); a.play().catch(() => {}); }}
            className="w-10 h-10 rounded-full bg-brand-600 flex items-center justify-center"
          >
            <Play size={18} fill="currentColor" />
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto grid grid-cols-3 gap-1 p-1">
        {(data?.items ?? []).map((s) => (
          <button
            key={s.id}
            className="relative aspect-[9/16] bg-surface-800 overflow-hidden"
            onClick={() => onOpenStory?.(s.id)}
          >
            <img src={s.thumbnailUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute bottom-1 left-1 text-[10px] bg-black/60 px-1 rounded">
              @{s.author.username}
            </div>
            <div className="absolute top-1 right-1 text-[10px] bg-black/60 px-1 rounded">
              ❤ {s.likeCount}
            </div>
          </button>
        ))}
      </div>

      {data && data.items.length === 0 && (
        <div className="flex-1 flex items-center justify-center text-surface-500 text-sm">
          Пока никто не использовал этот звук
        </div>
      )}
    </div>
  );
}

function formatDur(sec: number) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
